import { useState, useEffect, useMemo } from "react";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import { showSnackbar } from "@/lib/snackbarUtils";
import useCurrentUser from "@/hooks/useCurrentUser";
import { getBackendUri } from "@/lib/backendSource";
import { isDev } from "@/lib/urlUtils";

interface FacilityDataSyncDialogProps {
  open: boolean;
  onClose: () => void;
  facilityName: string;
  /** CRM facility legacy id (maps to CCMS facility_id for universal id lookup) */
  legacyId: string;
}

export default function FacilityDataSyncDialog({ open, onClose, facilityName, legacyId }: FacilityDataSyncDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken } = useCurrentUser();

  const isProduction = useMemo(() => !isDev(), []);

  useEffect(() => {
    if (open) {
      setError(null);
    }
  }, [open]);

  const sendSyncRequest = async () => {
    setError(null);
    setIsLoading(true);

    try {
      const backendUri = getBackendUri();
      if (!backendUri) {
        throw new Error("Backend URI not configured");
      }

      const authToken = await getAccessToken();
      if (!authToken) {
        throw new Error("Authentication required. Please log in.");
      }

      const digitsOnly = legacyId.replace(/\D/g, "");

      if (digitsOnly.length < 1 || digitsOnly.length > 10) {
        throw new Error("Invalid facility legacy ID. Must be 1 to 10 digits.");
      }

      const headers = {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json"
      };

      const url = `${backendUri}/api/servicebus/facilities/${digitsOnly}`;

      const response = await fetch(url, {
        method: "POST",
        headers
      });

      if (!response.ok) {
        let errorMessage = "Facility sync failed. Please try again.";
        try {
          const errorData = await response.json();
          if (errorData.errors) {
            errorMessage = `Facility sync failed: ${errorData.errors}`;
          }
        } catch (e) {
          console.error("ServiceBus facility sync: Failed to parse error response", e);
        }
        throw new Error(errorMessage);
      }

      await response.json().catch(() => ({}));

      showSnackbar("ServiceBus facility sync request sent");
      setError(null);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to send sync request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const digitsOnly = legacyId.replace(/\D/g, "");

  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline="Facility Data Sync"
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={() => void sendSyncRequest()}
            disabled={isLoading || digitsOnly.length < 1 || digitsOnly.length > 10}
            icon={isLoading ? "progress_activity" : undefined}
            iconClassName={isLoading ? "animate-spin w-6 h-6 !block" : undefined}
          >
            Confirm Sync
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {isProduction ? (
          <div>
            <Banner
              inline
              variant="error"
              description="Production Environment"
              subText="Request will synchronize production data"
            />
          </div>
        ) : (
          <div>
            <Banner
              inline
              variant="info"
              description="Development Environment"
              subText="Request will synchronize development data"
            />
          </div>
        )}

        <div className="text-body-medium">
          Are you sure you want to send a ServiceBus sync request for{" "}
          <strong>
            {facilityName} (facility legacy ID: {digitsOnly || "—"})
          </strong>
          ? The backend resolves the ServiceBus universal ID from CCMS. This triggers facility data synchronization.
        </div>

        {error && (
          <div>
            <Banner
              inline
              variant="error"
              description="Request Failed"
              subText={error}
              onClose={() => setError(null)}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
}
